<template>
  <section>
    <b-button @click="$bvModal.show(modalId)" size="sm" variant="primary" class="mr-2">
      <i class="fa fa-plus" aria-hidden="true"></i>
      Add New
    </b-button>

    <b-modal size="xl" :id="modalId" :ref="modalId" @show="resetModal">
      <template slot="modal-header">
        <h5 class="modal-title">Add Work Order</h5>
      </template>
      <div>
        <h6>1. Select a test house to perform the work order.</h6>
        <b-row class="mb-2">
            <b-col cols="5">
              <b-form-group label="Test House" class="required">
                <vue-simple-suggest
                  class="suggest-input form-control"
                  :class="{ 'is-invalid': $v.soInfoObj.companyId.$error }"
                  :styles="autoCompleteStyle"
                  :list="getListOfNames"
                  :max-suggestions="20"
                  :min-length="3"
                  :debounce="200"
                  :filter-by-query="true"
                  value-attribute="id"
                  display-attribute="name"
                  v-model="soInfoObj.companyName"
                  @select="onSuggestNameSelect"
                  @input="suggestNameChange"
                  @blur="$v.soInfoObj.companyId.$touch()"
                  @request-done="onRequestDone"
                >
                  <div slot="misc-item-below" v-if="isLoading" class="misc-item">
                    <span>Loading vendors...</span>
                  </div>
                </vue-simple-suggest>
                <div class="invalid-feedback">
                  <div v-if="!$v.soInfoObj.companyId.required">Test House is required</div>
                </div>
              </b-form-group>
            </b-col>
            <b-col cols="3">
              <b-form-group label="Contact">
                  <b-form-select v-model="soInfoObj.contactId">
                    <option v-for="(select, index) in sortSelect(contacts,'firstName')" :key="index" :value="select.id">
                      {{ select.firstName + ' ' + select.lastName }}
                    </option>
                  </b-form-select>
              </b-form-group>
            </b-col>
            <b-col cols="2">
              <b-form-group label="Date" class="required">
                <date-picker v-model="soInfoObj.soDate" :config="datePickerOptions" size="sm" :class="{ 'is-invalid': $v.soInfoObj.soDate.$error }"/>
                <div class="invalid-feedback">
                  <div v-if="!$v.soInfoObj.soDate.required">Date is required</div>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <h6>2. Select a purchase order listed below. </h6>
          <div class="d-flex justify-content-start align-items-end mb-2">
            <b-form-group label="Purchase Order Number" class="mr-2">
              <b-form-input v-model="filterObj.poNumber"  @input="onSearch"  size="sm" />
            </b-form-group>
            <b-form-group label="Company" class="mr-2">
              <b-form-input v-model="filterObj.companyName"  @input="onSearch" size="sm" />
            </b-form-group>
            <b-form-group label="Sales Rep" class="mr-2">
              <b-form-input v-model="filterObj.salesRep"  @input="onSearch" size="sm" />
            </b-form-group>
            <date-range-picker :dateFrom="filterObj.dateFrom" :dateTo="filterObj.dateTo" @handleSearch="onSearch"></date-range-picker>
            <b-button class="mr-2" size="sm" @click="clearFilter">Clear filters</b-button>
          </div>
          <grid-component
            isResizable
            isReorderable
            isSortable
            :gridId="gridId"
            :pageable="pageable"
            :skip="pageInfo.pageSize * (pageInfo.pageNumber - 1)"
            :take="pageInfo.pageSize"
            :itemData="itemData"
            :pageSizes="pageInfo.pageSize"
            :totalItems="pageInfo.totalItems"
            :fields="fields"
            :sortBy="pageInfo.sortBy"
            :sortOrder="pageInfo.sortOrder"
            :sortMethod="sortGrid"
            :onEdit="onEdit"
            :handleRowClick="onRowClick"
            @getData="onGetData"
            :isTblLoader="tblLoader"
          />
          <h6>3. Select a purchase order line.</h6>
          <purchase-order-lines
            :purchaseOrderId="selectedRowItemId"
            @selectedPurchaseOrderLine="selectedPOLine = $event; soInfoObj.qtyToTest = ($event != null) ? $event.quantity : 0"
          />

          <div v-if="Object.keys(selectedPOLine).length > 0">
            <h6>4. Review details.</h6>
            <line-info isPOLineObj :detailObj="selectedPOLine" />
          </div>
      </div>
      <template slot="modal-footer">
        <div class="mr-auto d-flex justify-content-start align-items-end">
          <b-form-group label="Qty To Test" class="mr-2 required">
              <b-form-input type="number" v-model="soInfoObj.qtyToTest" />
          </b-form-group>
          <b-form-group label="Instructions" style="width: 500px;">
              <b-form-input v-model="soInfoObj.comments" />
          </b-form-group>
        </div>
        
        <b-button @click="$bvModal.hide(modalId)">Cancel</b-button>
        <b-button variant="primary" @click="saveServiceOrder" :disabled="Object.keys(selectedPOLine).length == 0 || isSaving">
          <i v-if="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>Create Work Order</b-button>
      </template>
    </b-modal>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex';
import debounce from 'lodash/debounce';
import VueSimpleSuggest from 'vue-simple-suggest';
import { required } from 'vuelidate/lib/validators';
import { GRID_ID, DATE_FORMAT, GRID_PAGE_SIZES, AUTO_COMPLETE_STYLES,SEARCH_DEBOUNCE_TIME,VENDOR_ID } from '@/shared/constants';
import GridComponent from '@/shared/components/GridComponent';
import gridFields from '@/pages/PurchaseOrders/fields';
import linesFields from './fields';
import DateRangePicker from '@/shared/components/DateRangePicker/IndexNew';
import PurchaseOrderLines from '@/pages/PurchaseOrders/components/PurchaseOrderLines';
import LineInfo from '@/pages/ServiceOrders/components/edit/components/ServiceOrderLineInfo';

export default {
  components: {
    DateRangePicker,
    VueSimpleSuggest,
    GridComponent,
    PurchaseOrderLines,
    LineInfo
  },
  data() {
    return {
      linesFields,
      modalId: 'add-service-order-modal',
      fields: gridFields,
      gridId: GRID_ID.PURCHASE_ORDERS,
      poLineGridId: GRID_ID.PURCHASE_ORDER_LINES_FOR_SERVICE_ORDER,
      pageable: {
        pageSizes: GRID_PAGE_SIZES,
      },
      selectedRowItemId: 0,
      checkedRowItemIds: [],
      selectedAccount: {},
      soInfoObj: {
        companyId: null,
        contactId: null,
        comments: null,
        soDate: new Date(),
        qtyToTest: null
      },
      chosenName:  '',
      autoCompleteStyle: AUTO_COMPLETE_STYLES,
      datePickerOptions: {
          format: DATE_FORMAT,
          useCurrent: true,
      },
      isLoading: false,
      isSaving: false,
      selectedPO: {},
      selectedPOLine: {},
      filterPageName: 'addSoModal'
    };
  },
  validations: {
    soInfoObj: {
      companyId: { required },
      soDate: { required },
      qtyToTest: { required }
    },
  },
  computed: {
//    ...mapState('purchaseorders', ['lastOpenedId', 'purchaseorders']),
    ...mapState('salesReps', ['salesReps']),
    ...mapGetters('purchaseorders', [
      //'purchaseorder',
      'gridList',
      'pageInfo',
      'filters',
      'poLinesByPoId',
      'tblLoader'
    ]),
    // ...mapGetters('quoteAdd', [
    //   'customers',
    //   'vendors',
    //   'contacts',
    // ]),
    ...mapGetters('quoteAdd', [
      'contacts',
    ]),
    ...mapGetters('account', ['gridSettingsById']),
     gridSettings() {
      return this.gridSettingsById(this.gridId);
    },
    itemData() {
      return this.gridList.map((purchaseorder) => ({ ...purchaseorder, selected: purchaseorder.id === this.selectedRowItemId }));
    },
    poLineData() {
      return this.poLinesByPoId.map((purchaseorder) => ({
        ...purchaseorder,
        qtyToTest: this.soInfoObj.qtyToTest
      }));
    },
    filterObj() {
      return this.filters[this.filterPageName] || {}
    }
  },
  methods: {
    ...mapMutations('purchaseorders', [ 'setItem' ]),
    ...mapActions('purchaseorders', ['getAll','sortGrid','search','getByPurchaseOrderId']),
    ...mapActions('account', ['saveGridSettings']),
    ...mapActions('companies', ['getByNameAndAccountType']),
    ...mapActions('serviceOrders', ['createServiceOrderFromPO']),
    onSearch: debounce(function(data = false) {
        if(typeof data === 'object') { Object.assign(this.filtersObj,data) }
        this.search({'filters':this.filtersObj,page:this.filterPageName});
    }, SEARCH_DEBOUNCE_TIME),

    // ...mapMutations('quoteAdd', ['clearCompanyQuoteLines','filterLinesByContactId']),
    // ...mapActions('quoteAdd', [
    //   'getCustomers',
    //   'getContacts',
    //   'clearContacts',
    // ]),
    ...mapMutations('quoteAdd',{quoteAddSetItem: 'setItem'}),
    ...mapActions('quoteAdd', [
      'getContacts',
    ]),

    clearFilter() {
        this.clearObject(this.filtersObj);
        this.search({'filters':this.filterObj,page:this.filterPageName});
    },
    async onRowClick(row) {
      if(this.selectedRowItemId != row.dataItem.id){
        this.selectedRowItemId = row.dataItem.id;
        this.selectedPO =  row.dataItem;
        this.selectedPOLine = {};

        // await this.getByPurchaseOrderId(row.dataItem.id);
      }
    },
    onEdit(row) {
      this.setItem({ resource: 'lastOpenedId', payload: row.id });
      this.$router.push({
        name: 'purchaseorders.edit',
        params: { id: row.id },
      });
    },
    async onGetData(params) {
      await this.getAll(params);
    },
    async onSuggestNameSelect(suggestion) {
      this.selectedAccount = suggestion;
      this.soInfoObj.companyId = suggestion.id;
      await this.getContacts(suggestion.id);
    },
    async suggestNameChange(val) {
      this.isLoading = true;
      if (val === '') {
        this.resetModal();
      }
    },
    async getListOfNames(name) {
      return await this.getByNameAndAccountType({
        typeId: VENDOR_ID, name});
    },
    async resetModal() {
      Object.keys(this.soInfoObj).forEach(soKey => {
        this.soInfoObj[soKey] = null;
      });
      this.soInfoObj.soDate = new Date();
      this.setItem({ resource: 'poLinesByPoId', payload: [] });
      // this.clearContacts();
      this.quoteAddSetItem({ resource: 'contacts', payload: [] });
    },
    onRequestDone() {
      this.isLoading = false;
    },
    async saveServiceOrder() {
      this.$v.soInfoObj.$touch();
      if (this.$v.soInfoObj.$invalid) {
        return;
      }
        const data = {
          poToServiceOrder: {
            ...this.selectedPO,
            id: 0,
            purchaseOrderId:this.selectedPO.id,
            companyId: this.soInfoObj.companyId,
            contactId: this.soInfoObj.contactId,
            soDate: this.soInfoObj.soDate
          },
          poLinesToServiceOrderLines: [
            {
              purchaseOrderLineId: this.selectedPOLine.id,
              quantity: this.soInfoObj.qtyToTest,
              comments: this.soInfoObj.comments,
              id: 0
            }
          ]
        };
      try {
        this.isSaving = true;
        const id = await this.createServiceOrderFromPO(data);
        this.isSaving = false;
        this.$emit('OnAddItem');
        this.$bvModal.hide(this.modalId);
        this.resetModal();
        if (id > 0) {
          this.$router.push({name: 'serviceorders.edit', params: {id}});
        }
      } catch (e) {
        this.isSaving = false;
        /*console.log(e.message);*/
      }
    }
  },
  watch: {

  },
  async mounted() {
    this.setItem({ resource:'filterPage', payload: this.filterPageName });
    this.setItem({ resource: 'pageInfo', payload: {...this.pageInfo, ...this.gridSettings.sort, ...this.gridSettings.pageSize} });
    await this.getAll({});
  },
  beforeDestroy() {
    //this.select({ id: null, isChecked: false });
  },

};
</script>

<style lang="scss">
@import '@/assets/scss/_colors.scss';

.highlight {
  background: $rowSelection !important;
}

.b-pagination {
  justify-content: center;
}

.tools > * {
  margin: 0 0 0 3px;
}

#KendoGrid .k-virtual-scrollable-wrap table {
  min-width: 800px;
}

.max-grid-height {
  max-height: 280px;
}
</style>
